import { registerWithSSO, useRegisterMutation } from "../api/api";

import { GoogleLogin } from "@react-oauth/google";
import { Button, Input } from "antd";
import { useFormik } from "formik";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useNotifications } from "../components/notifications/notification";
import { useAuthContext } from "../providers/auth.provider";
import { EXHUT_WEB_LOCAL_STORAGE_TOKEN } from "../utils/constants";

export default function RegisterPage() {
  const { mutateAsync, isPending } = useRegisterMutation();
  const { mutateAsync: mutateRegisterWithSSOAsync } = registerWithSSO();
  const { setUser } = useAuthContext();
  const { notification, notifyError } = useNotifications();

  const navigate = useNavigate();

  const [isSuccess, setIsSuccess] = useState<boolean>();
  const [errorMsg, setErrorMsg] = useState<string>();
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },

    validationSchema: Yup.object({
      email: Yup.string()
        .email("Please enter a valid email address.")
        .required("Please enter email."),

      password: Yup.string()
        .min(8, "Password must be at least 8 characters.")
        .required("Please enter password."),
    }),
    onSubmit: (values) => {
      setErrorMsg(undefined);
      setIsSuccess(undefined);
      mutateAsync({
        email: values.email,
        password: values.password,
        source: "expense-tracker",
      })
        .then((result) => {
          setIsSuccess(true);
        })
        .catch((err) => {
          let message =
            err.response?.data?.message ||
            "Sorry! Something went wrong. Please try again.";
          setIsSuccess(false);
          setErrorMsg(message);
        });
    },
  });

  const handleRegisterWithSSO = (token?: string) => {
    setErrorMsg(undefined);

    if (!token) {
      notifyError("Whoops! Please try again.");
    }

    mutateRegisterWithSSOAsync({ token: token! })
      .then((result) => {
        localStorage.setItem(EXHUT_WEB_LOCAL_STORAGE_TOKEN, result.sign);
        setUser(result);
        navigate("/home");
      })
      .catch((err) => {
        if (err.response.data.statusCode === 409) {
          setErrorMsg("This email address is already in use.");
        } else {
          setErrorMsg("Unable to register. Please contact help@expensehut.com");
        }
      });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-6 py-12">
      {notification}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/20 rounded-full blur-3xl -z-10"></div>

      <div className="w-full max-w-md">
        {/* Logo & Brand */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create Account
          </h1>
          <p className="text-muted-foreground">
            Join ExpenseHut to start managing expenses with your family
          </p>{" "}
        </div>

        <div className="my-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-900 leading-relaxed">
            <span className="font-semibold">Privacy First:</span> Your personal
            data is encrypted and secure. We never sell your information.
          </p>
        </div>

        {isSuccess && (
          <div className="bg-green-300/20 border border-green-500 rounded-lg p-4 text-sm my-6">
            A verification email has been sent to your email address. Please
            click the link on it to continue.
          </div>
        )}
        {errorMsg && (
          <div className="bg-red-300/20 border border-red-500 rounded-lg p-4 text-sm my-6">
            {errorMsg}
          </div>
        )}

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <form onSubmit={formik.handleSubmit} className="space-y-6">
            {/* Username Field */}
            <div className="space-y-2">
              Email
              <Input
                id="email"
                type="text"
                disabled={isPending}
                className="h-11 border-gray-300 focus-visible:ring-blue-500"
                {...formik.getFieldProps("email")}
              />
              {!!formik.errors.email && (
                <div className="my-2 text-red-500 text-xs">
                  {formik.errors.email}
                </div>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Password</span>
              </div>
              <Input
                id="password"
                type="password"
                disabled={isPending}
                className="h-11 border-gray-300 focus-visible:ring-blue-500"
                {...formik.getFieldProps("password")}
              />
              {!!formik.errors.password && (
                <div className="my-2 text-red-500 text-xs">
                  {formik.errors.password}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="primary"
              onClick={formik.submitForm}
              disabled={isPending}
              className="w-full h-11 font-semibold"
            >
              {isPending ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center gap-2">Create Account</span>
              )}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span className="text-xs text-muted-foreground">or</span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>

          {/* Login Link */}
          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold text-blue-600 hover:underline"
            >
              Sign in
            </Link>
          </p>

          <div className="my-3 flex justify-center">
            <GoogleLogin
              text="signup_with"
              onSuccess={(credentialResponse) => {
                handleRegisterWithSSO(credentialResponse.credential);
              }}
              onError={() => {
                notifyError("Google signin Failed");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
